import React, { PureComponent } from "react";
import type { ApolloClient } from "@apollo/client";
import {
  get,
  groupBy,
  includes,
  isArray,
  isEmpty,
  map,
  some,
  transform,
} from "lodash";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { AGENT_ROLE_ID } from "statics/roles";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { enableProductFeatures } from "actions/adminActions";
import {
  EmployerApplicationApproved,
  EmployerClosed,
  EmployerPendingKYBSubmission,
  EmployerRegistrationComplete,
  EmployerTermsComplete,
  getStatusLabel,
} from "statics/states";
import {
  getCompany,
  getCompanyAdminsAndAgents,
  getCompanyKybData,
  getCompanyMembers,
  getTakeRateForCompany,
} from "services/adminService";

import IconHeader from "components/IconHeader";
import InfoTable from "components/InfoTable";
import Button from "components/Button";
import ProductFeatures from "./ProductFeatures";
import BankLinkModal from "./BankLinkModal";
import DisableCompanyModal from "./DisableCompanyModal";
import EnableCompanyModal from "./EnableCompanyModal";
import UnfreezeCompanyModal from "./UnfreezeCompanyModal";
import FreezeCompanyModal from "./FreezeCompanyModal";
import IconSpinner from "components/IconSpinner";
import AdminsAndAgents from "./AdminsAndAgents";
import CompanyKyb from "pages/dashboards/adminDashboard/KYB/Company";

import "./EmployerInfo.scss";
import { getEmployerTakeRateDownloadUrl } from "services/documentService";
import { simulateLinkClickNewTab } from "utils/dom";
import { wrapError } from "utils/errorHelper";
import { FiDownload } from "react-icons/fi";
import Alert from "components/Alert";
import { Link } from "react-router-dom";
import PayrollIntegrationStatusField from "pages/dashboards/adminDashboard/employers/PayrollIntegrationStatusField";
import { payrollIntegrations360 } from "statics/featureIds";
import ProgressiveOnboardingStateField from "pages/dashboards/adminDashboard/employers/ProgressiveOnboardingStateField";
import { getApplicationQueueForEntity } from "utils/alloyHelpers";
import type { RootState } from "types/state";
import type { Company } from "types/models";

function buildProductFeaturesObject(
  features: ReadonlyArray<number | null> | null | undefined
): Record<string, boolean> {
  return transform(
    // lodash.transform's array overload doesn't accept null/undefined; the `!`
    // is compile-time only — runtime is unchanged (transform of a nullish
    // collection simply returns the accumulator).
    features as number[],
    (acc: Record<string, boolean>, val) => {
      acc[val] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
}

const errorSelector = createErrorSelector(
  adminConstants.ENABLE_PRODUCT_FEATURES
);
const loadingSelector = createLoadingSelector(
  adminConstants.ENABLE_PRODUCT_FEATURES
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isFetching: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  enableProductFeatures,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  /** Notifies the parent page whenever a freeze/unfreeze here changes
   *  freezeDetails.status, so its frozen banner tracks the button state. */
  onFreezeStatusChange?: (freezeStatus: string | null) => void;
  /** Notifies the parent page when a disable/enable here changes
   *  currentState, so its tab list and KYB content track the new state. */
  onCompanyStateChange?: (currentState: string) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

/** Company shape rendered here: the getCompany result merged with take-rate
 *  counts plus the derived silaWalletId / enabledProductFeatures-object set in
 *  componentDidMount. Accessed dynamically throughout render, so kept loose.
 *  enabledProductFeatures is the codegen array on the raw getCompany result but
 *  is replaced with the derived boolean-map in componentDidMount, so it can be
 *  either; freezeDetails is the raw query value or a lodash-get result. */
interface CompanyState {
  id?: string;
  name?: string | null;
  currentState?: string | null;
  payrollIntegrationStatus?: string | null;
  silaWalletId?: string;
  enabledProductFeatures?:
    | Record<string, boolean>
    | ReadonlyArray<number | null>
    | null;
  freezeDetails?: { status?: string; [key: string]: unknown } | null;
  [key: string]: unknown;
}

/** Shape of an InfoTable metadata row (mirrors InfoTable's non-exported
 *  InfoTableMeta; format callbacks receive unknown — same as InfoTable). */
interface MetadataRow {
  key?: string;
  label?: string;
  apiKey?: string;
  format?: string | ((data: unknown) => unknown);
  [key: string]: unknown;
}

/** A business member as returned by getCompanyMembers (services/adminService). */
type Member = NonNullable<
  NonNullable<Awaited<ReturnType<typeof getCompanyMembers>>>[number]
>;

/** Admins/agents as returned by getCompanyAdminsAndAgents. The query selects
 *  user/link non-null at runtime (codegen marks them nullable); modelled
 *  non-null here so the groupBy callback and AdminsAndAgents agree. */
interface Administrator {
  user: {
    id: string;
    email: string;
    username: string;
    userRoleId: string;
  };
  link: {
    id: string;
    invitationExpiresAt: string;
    status: string;
    companyId: string;
    locked: boolean;
  };
}

interface State {
  company: CompanyState;
  // present in initial state but never read; preserved verbatim
  takeRate: Record<string, unknown>;
  showLinkModal: boolean;
  showEditForm: boolean;
  isFetching: boolean;
  // present in initial state but never read; written by the dead _onOrgCreation
  organization: Record<string, unknown>;
  members: Member[];
  downloadError: string | null;
  showUnfreezeModal: boolean;
  showFreezeModal: boolean;
  administrators?: Administrator[];
  showDisableModal?: boolean;
  showEnableModal?: boolean;
  fetchingAdmins?: boolean;
  // read in render (line ~584) but never written; left optional
  disablingCompany?: boolean;
}

class EmployerInfo extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      company: {},
      takeRate: {},
      showLinkModal: false,
      showEditForm: false,
      isFetching: true,
      organization: {},
      members: [],
      downloadError: null,
      showUnfreezeModal: false,
      showFreezeModal: false,
    };
  }

  async componentDidMount() {
    const client = this.props.client as ApolloClient<object>;
    const companyId = this.props.companyId as string;
    const resolved =
      // @ts-expect-error Promise.props is a Bluebird runtime augmentation with
      // no TypeScript types in this project; the resolved object is untyped.
      await Promise.props({
        company: getCompany(client, companyId),
        members: getCompanyMembers(client, companyId),
        takeRate: getTakeRateForCompany(client, companyId),
        kybInfo: getCompanyKybData(client, companyId),
        administrators: getCompanyAdminsAndAgents(client, companyId),
      });
    const {
      company,
      takeRate = {},
      administrators,
      members = [],
      kybInfo,
    } = resolved;

    this.setState({
      company: {
        ...company,
        ...takeRate,
        silaWalletId: get(kybInfo, "silaWallet.accountNumber", ""),
        enabledProductFeatures: buildProductFeaturesObject(
          company.enabledProductFeatures
        ),
      },
      isFetching: false,
      administrators,
      showDisableModal: false,
      members,
    });
  }

  _onOrgCreation = (organization: Record<string, unknown>) => {
    this.setState({ organization });
  };

  _getKybButton = () => {
    const companyState = this.state.company.currentState;

    const baseUrl = `/employers/${this.props.companyId}/kyb/`;
    switch (companyState) {
      case EmployerTermsComplete:
        return (
          <Button
            size="sm"
            name="action"
            onClick={() => this.props.push(baseUrl + "summary")}
            btnLabel="KYB Summary"
          />
        );
      case EmployerRegistrationComplete:
        return (
          <Button
            size="sm"
            name="action"
            onClick={() => this.props.push(baseUrl + "summary")}
            btnLabel="KYB Summary"
          />
        );
      case EmployerPendingKYBSubmission:
        return (
          <Button
            size="sm"
            name="action"
            onClick={() => this.props.push(baseUrl + "summary")}
            btnLabel="KYB Summary"
          />
        );
      case EmployerApplicationApproved:
        return (
          <Button
            size="sm"
            name="action"
            onClick={() => this.setState({ showEditForm: true })}
            btnLabel="Edit Info"
            color="secondary"
          />
        );
      default:
        return null;
    }
  };

  _closeLinkModal = () => {
    this.setState({ showLinkModal: false });
  };

  _showLinkModal = () => {
    this.setState({ showLinkModal: true });
  };

  _showDisableModal = () => {
    this.setState({ showDisableModal: true });
  };
  _showUnfreezeModal = () => {
    this.setState({ showUnfreezeModal: true });
  };
  _showFreezeModal = () => {
    this.setState({ showFreezeModal: true });
  };
  _closeDisableEmployerModal = () => {
    this.setState({ showDisableModal: false });
  };
  _onDisableSuccess = (currentState: string) => {
    this._applyCompanyState(currentState, { showDisableModal: false });
  };
  _showEnableModal = () => {
    this.setState({ showEnableModal: true });
  };
  _closeEnableEmployerModal = () => {
    this.setState({ showEnableModal: false });
  };
  _onEnableSuccess = (currentState: string) => {
    this._applyCompanyState(currentState, { showEnableModal: false });
  };

  /** Both lifecycle modals resolve to the company's new state: store it and
   *  hand it to the parent, whose tab list is derived from the same value. */
  _applyCompanyState = (
    currentState: string,
    modalState: Pick<State, "showDisableModal"> | Pick<State, "showEnableModal">
  ) => {
    this.setState({
      ...modalState,
      company: { ...this.state.company, currentState },
    });
    this.props.onCompanyStateChange?.(currentState);
  };

  _closeUnfreezeModal = async (hasSubmitted = false) => {
    if (!hasSubmitted) {
      return this.setState({ showUnfreezeModal: false });
    }
    const company = await getCompany(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    );
    const freezeDetails = get(
      company,
      "freezeDetails",
      {}
    ) as CompanyState["freezeDetails"];
    this.setState({
      showUnfreezeModal: false,
      company: {
        ...company,
        freezeDetails,
      },
    });
    this.props.onFreezeStatusChange?.(company.freezeDetails?.status ?? null);
  };

  _closeFreezeModal = async (hasSubmitted = false) => {
    if (!hasSubmitted) {
      return this.setState({ showFreezeModal: false });
    }
    const company = await getCompany(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    );
    const freezeDetails = get(
      company,
      "freezeDetails",
      {}
    ) as CompanyState["freezeDetails"];
    this.setState({
      showFreezeModal: false,
      company: {
        ...company,
        freezeDetails,
      },
    });
    this.props.onFreezeStatusChange?.(company.freezeDetails?.status ?? null);
  };

  _refetchAdminsAndAgents = async () => {
    this.setState({
      fetchingAdmins: true,
    });
    const administrators = (await getCompanyAdminsAndAgents(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
      // query selects user/link non-null at runtime; codegen marks them nullable
    )) as unknown as Administrator[];

    this.setState({
      administrators,
      fetchingAdmins: false,
    });
  };

  _updateProductFeatures = (enabledFeatures: number[]) => {
    void this.props
      .enableProductFeatures(this.props.client as ApolloClient<object>, {
        // companyId comes from the /employers/:id route param — always present here
        companyId: this.props.companyId as string,
        featureIds: enabledFeatures,
      })
      .then((result) => {
        if (result.success) {
          toast.success("Successfully updated enabled product features.");
        }
      });
  };

  _downloadFile(status: string) {
    getEmployerTakeRateDownloadUrl(this.props.client as ApolloClient<object>, {
      groupId: null,
      status,
      companyId: this.props.companyId,
      timeframe: "ALL_TIME",
    })
      .then((response) => {
        // original JS accessed response.url unguarded; the `!` is compile-time
        // only and preserves the original throw-on-null behavior
        simulateLinkClickNewTab(response!.url);
      })
      .catch((error) => {
        this.setState({ downloadError: wrapError(error) });
      });
  }

  _buildTakeRateItems = (): MetadataRow[] => {
    const takeRateItems = [
      {
        key: "notStarted",
        apiKey: "NOT_STARTED",
        label: "Not Enrolled",
      },
      {
        key: "opening",
        apiKey: "OPENING",
        label: "Enrolling",
      },
      {
        key: "enrolled",
        apiKey: "ENROLLED",
        label: "Enrolled",
      },
    ];

    return map(takeRateItems, (item) => {
      return {
        ...item,
        format: (val: unknown) => {
          return (
            <div>
              <span>{val as React.ReactNode}</span>
              <a
                className="link"
                target="_blank"
                onClick={() => this._downloadFile(item.apiKey)}
              >
                <FiDownload
                  fill={"#FFFFFF"}
                  color={"#FFFFFF"}
                  stroke={"#29b0c2"}
                  strokeWidth={"1.5"}
                  size={"16px"}
                />
              </a>
            </div>
          );
        },
      };
    });
  };

  onPayrollIntegrationSuccess = (status: string | null) => {
    this.setState({
      company: {
        ...this.state.company,
        payrollIntegrationStatus: status,
      },
    });
  };

  // The edit/freeze/unfreeze refetches put the raw id array back in place of
  // the boolean map built in componentDidMount, so accept either shape.
  hasPayrollIntegration360 = () => {
    const features = this.state.company.enabledProductFeatures;
    return some(payrollIntegrations360, (featureId) =>
      isArray(features)
        ? includes(features, featureId)
        : Boolean((features as Record<string, boolean> | null)?.[featureId])
    );
  };

  onLaunchPlanSuccess = (progressiveOnboardingState: string) => {
    this.setState({
      company: {
        ...this.state.company,
        progressiveOnboardingState,
      },
    });
  };

  getPayrollIntegrationStatusField = (): MetadataRow | null => {
    const payrollIntegrationStatus = get(
      this.state.company,
      "payrollIntegrationStatus",
      null
    );

    if (!payrollIntegrationStatus) {
      return null;
    }

    return {
      key: "payrollIntegrationStatus",
      label: "Payroll Integration Status",
      format: (status: unknown) => {
        return (
          <PayrollIntegrationStatusField
            status={status as string}
            companyId={this.props.companyId}
            onSuccess={this.onPayrollIntegrationSuccess}
          />
        );
      },
    };
  };

  _getMetadata = (): MetadataRow[] => {
    const membersSection = map(this.state.members, (member, idx) => {
      return {
        key: `members.${idx}`,
        label: `Member #${idx + 1}`,
        format(data: unknown) {
          const val = data as Member;
          // memberProfile is nullable in codegen but always present at runtime
          const { firstName, lastName } = val.memberProfile!;
          if (firstName && lastName) {
            const url = getApplicationQueueForEntity(
              `${firstName} ${lastName}`
            );
            return (
              <a
                className="icon-link"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {firstName} {lastName} - (View in Alloy)
              </a>
            );
          }
          return `${firstName} ${lastName}`;
        },
      };
    });
    const payrollIntegrationStatusField =
      this.getPayrollIntegrationStatusField();
    return [
      {
        key: "name",
        label: "Company Name",
      },
      {
        key: "dba",
        label: "Company DBA",
      },
      {
        key: "ein",
        label: "Company EIN",
      },
      {
        key: "id",
        label: "Company ID",
      },

      {
        key: "user.id",
        label: "Company Admin Id",
      },
      {
        key: "user.email",
        label: "Company Admin Email",
      },

      {
        key: "currentState",
        label: "State",
        format(data: unknown) {
          const state = data as string;
          const friendlyLabel = getStatusLabel(state);
          const stateLabel = state ? state : "None";
          return `${friendlyLabel}(${stateLabel})`;
        },
      },
      {
        key: "progressiveOnboardingState",
        label: "Progressive Onboarding State",
        format: (state: unknown) => (
          <ProgressiveOnboardingStateField
            companyId={this.props.companyId as string}
            progressiveOnboardingState={state as string | null}
            payrollIntegrationStatus={
              this.state.company.payrollIntegrationStatus
            }
            hasPayrollIntegration={this.hasPayrollIntegration360()}
            onSuccess={this.onLaunchPlanSuccess}
          />
        ),
      },
      ...(payrollIntegrationStatusField ? [payrollIntegrationStatusField] : []),
      {
        key: "planId",
        label: "Plan ID",
      },
      {
        key: "payrollProvider",
        label: "Payroll Provider",
      },
      {
        key: "billingType",
        label: "Billing Type",
      },
      {
        key: "stripeCustomerId",
        label: "Stripe Customer ID",
      },
      ...this._buildTakeRateItems(),

      {
        key: "silaWalletId",
        label: "Sila Wallet ID",
      },
      ...membersSection,
      {
        key: "freezeStatus",
        label: "Freeze Status",
      },
      {
        key: "alloyView",
        label: "Alloy URL",
        format: () => {
          const companyName = get(this.state.company, "name", "") as string;
          const alloyUrl = getApplicationQueueForEntity(companyName);
          return (
            <a
              className="icon-link"
              href={alloyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Alloy
            </a>
          );
        },
      },
    ];
  };

  getIsFrozenStatus = () => {
    if (isEmpty(this.state.company.freezeDetails)) {
      return "UNFROZEN";
    }
    // isEmpty guard guarantees freezeDetails is present here
    return this.state.company.freezeDetails!.status;
  };

  render() {
    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }

    const metadata = this._getMetadata();

    const { agents, administrators } = groupBy(
      this.state.administrators,
      (admin: Administrator) => {
        if (admin.user.userRoleId === AGENT_ROLE_ID) {
          return "agents";
        }
        return "administrators";
      }
    );

    if (this.state.showEditForm) {
      return (
        <CompanyKyb
          companyId={this.state.company.id}
          hasPassedKyb={true}
          onUpdate={async () => {
            this.setState({ showEditForm: false });
            const company = await getCompany(
              this.props.client as ApolloClient<object>,
              this.props.companyId as string
            );
            this.setState({
              company: {
                ...this.state.company,
                ...company,
              },
            });
          }}
          onBack={() => {
            this.setState({ showEditForm: false });
          }}
        />
      );
    }

    return (
      <>
        {this.state.showLinkModal && (
          <BankLinkModal
            companyId={this.props.companyId}
            onClose={this._closeLinkModal}
          />
        )}
        {this.state.showDisableModal && (
          <DisableCompanyModal
            // DisableCompanyModal tightened company to the full Company model;
            // only the fields present on our loose state object are read
            company={this.state.company as unknown as Company}
            onClose={this._closeDisableEmployerModal}
            onSuccess={this._onDisableSuccess}
          />
        )}
        {this.state.showEnableModal && (
          <EnableCompanyModal
            company={this.state.company as unknown as Company}
            onClose={this._closeEnableEmployerModal}
            onSuccess={this._onEnableSuccess}
          />
        )}
        {this.state.showUnfreezeModal && (
          <UnfreezeCompanyModal
            // UnfreezeCompanyModal (sibling, not owned here) tightened
            // freezeDetails to { reason?: FreezeReason }; our loose state
            // freezeDetails has no overlap. The component only reads
            // company.id and company.freezeDetails.reason via lodash get, so
            // the runtime object is passed through unchanged.
            company={
              this.state.company as React.ComponentProps<
                typeof UnfreezeCompanyModal
              >["company"]
            }
            onClose={this._closeUnfreezeModal}
          />
        )}
        {this.state.showFreezeModal && (
          <FreezeCompanyModal
            company={this.state.company}
            onClose={this._closeFreezeModal}
          />
        )}

        <div className="action-box">
          <div className="action-header">
            <span>
              <IconHeader
                variant="cardHeader"
                headerText="Employer Information"
              />
            </span>

            <div>
              {get(this.state.company, "freezeDetails.status") === "FROZEN" && (
                <Button
                  size="sm"
                  name="action"
                  loading={this.state.showUnfreezeModal}
                  onClick={this._showUnfreezeModal}
                  disabled={
                    isEmpty(this.state.company.freezeDetails) ||
                    this.state.company.freezeDetails!.status !== "FROZEN"
                  }
                  btnLabel="Unfreeze Company"
                  color="red"
                />
              )}
              {get(this.state.company, "freezeDetails.status", "UNFROZEN") ===
                "UNFROZEN" && (
                <Button
                  size="sm"
                  name="action"
                  loading={this.state.showFreezeModal}
                  onClick={this._showFreezeModal}
                  disabled={
                    get(
                      this.state.company,
                      "freezeDetails.status",
                      "UNFROZEN"
                    ) !== "UNFROZEN"
                  }
                  btnLabel="Freeze Company"
                  color="red"
                />
              )}
              {this.state.company.currentState ===
                EmployerApplicationApproved && (
                <Button
                  size="sm"
                  name="action"
                  loading={this.state.disablingCompany}
                  onClick={this._showDisableModal}
                  btnLabel="Disable Company"
                  color="red"
                />
              )}
              {this.state.company.currentState === EmployerClosed && (
                <Button
                  size="sm"
                  name="action"
                  onClick={this._showEnableModal}
                  btnLabel="Enable Company"
                />
              )}
              <span> {this._getKybButton()}</span>

              <span style={{ paddingLeft: 5 }}>
                <Button
                  size="sm"
                  name="action"
                  onClick={this._showLinkModal}
                  disabled={
                    this.state.company.currentState !==
                    EmployerApplicationApproved
                  }
                  btnLabel="Link Bank"
                />
              </span>
            </div>
          </div>
          <div className="action-body">
            <InfoTable
              metadata={metadata}
              data={{
                ...this.state.company,
                members: this.state.members,
                freezeStatus: this.getIsFrozenStatus(),
              }}
            />
            {this.state.downloadError && (
              <Alert msg={this.state.downloadError} type={"error"} />
            )}
            <Link
              to={`/dashboard/reports/custom/new?employerId=${this.props.companyId}`}
              className="link"
              style={{ paddingLeft: 0, marginTop: 8, display: "inline-block" }}
            >
              Create Contribution Report
            </Link>
            <div style={{ textAlign: "right" }}>
              <Link
                to={`/workflows?entityId=${this.props.companyId}`}
                className="icon-link"
              >
                View Workflows
              </Link>
            </div>
          </div>
        </div>
        <ProductFeatures
          companyId={this.props.companyId}
          error={this.props.error}
          isFetching={this.props.isFetching}
          onChange={this._updateProductFeatures}
          // ProductFeatures (sibling, not owned here) types this prop as
          // Record<string, boolean>; our state field is the wider codegen
          // union, but componentDidMount always stores the boolean-map built
          // by buildProductFeaturesObject. Value passed through unchanged.
          enabledProductFeatures={
            this.state.company.enabledProductFeatures as
              | Record<string, boolean>
              | undefined
          }
        />
        <AdminsAndAgents
          agents={agents}
          administrators={administrators}
          companyId={this.props.companyId}
          fetchAdmins={this._refetchAdminsAndAgents}
        />
      </>
    );
  }
}

export default connector(withApollo<Props>(EmployerInfo));
